import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";

export const getLineSchemeForDate = async (
	assetId: number,
	date: string,
	signal?: AbortSignal,
): Promise<number | null> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "AssetId", value: assetId },
		{ name: "Date", value: date },
	];
	const resp = await apiService.callV2(
		"[FAB].[GetLineSchemeForDate]",
		parameters,
		signal,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching line scheme");
	}

	const rows = get(resp, "data.tables[0].rows", []) as {
		SchemeAssetId: number | null;
	}[];
	return rows[0]?.SchemeAssetId ?? null;
};

export interface LineScheduleDayResult {
	LineScheduleDayId: number | null;
	AssetId: number;
	ProductionDate: string;
	SourceAssetId: number | null;
	/** set when the day changed shape and entered the calendar Undo/Redo stack */
	PlanChangeId: number | null;
}

export const upsertLineScheduleDay = async ({
	assetId,
	productionDate,
	shiftsPerDay,
	applyScheme = true,
	shiftSpells = [],
}: {
	assetId: number;
	productionDate: string;
	/** 2 or 3; null = the line's default scheme */
	shiftsPerDay: number | null;
	applyScheme?: boolean;
	shiftSpells?: ShiftSpellChange[];
}): Promise<LineScheduleDayResult | null> => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "AssetId", value: assetId },
		{ name: "ProductionDate", value: productionDate },
		{ name: "ShiftsPerDay", value: shiftsPerDay },
		{ name: "ApplyScheme", value: applyScheme },
		{ name: "ShiftSpells", value: JSON.stringify(shiftSpells) },
	];

	const resp = await apiService.callV2(
		"[FAB].[UpsertLineScheduleDay]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error saving line schedule day");
	}

	const rows = get(resp, "data.tables[0].rows", []) as LineScheduleDayResult[];
	return rows[0] ?? null;
};

export type ShiftSpellChange = { shiftId: number; isSpell: boolean };

export interface DayShiftOption {
	ShiftId: number;
	Shift: string;
	Start: string;
	End: string;
	ShiftHours: 8 | 12;
	BreakCount: number;
	SpellCount: number;
}

export const getDayShiftOptions = async ({
	assetId,
	productionDate,
	shiftsPerDay,
}: {
	assetId: number;
	productionDate: string;
	shiftsPerDay: number | null;
}): Promise<DayShiftOption[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "AssetId", value: assetId },
		{ name: "ProductionDate", value: productionDate },
		{ name: "ShiftsPerDay", value: shiftsPerDay },
	];
	const resp = await apiService.callV2(
		"[FAB].[GetDayShiftOptions]",
		parameters,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching day shifts");
	}
	return get(resp, "data.tables[0].rows", []) as DayShiftOption[];
};

/** records of the line on that day that a shape change would re-stamp */
export interface LineScheduleDayImpact {
	Source: string;
	Cnt: number;
}

export const getLineScheduleDayImpact = async (
	assetId: number,
	productionDate: string,
	signal?: AbortSignal,
): Promise<LineScheduleDayImpact[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "AssetId", value: assetId },
		{ name: "ProductionDate", value: productionDate },
	];
	const resp = await apiService.callV2(
		"[FAB].[GetLineScheduleDayImpact]",
		parameters,
		signal,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching day config impact");
	}
	return get(resp, "data.tables[0].rows", []) as LineScheduleDayImpact[];
};
